/**
 * ChangePasswordModal - Модальное окно для смены пароля текущего пользователя
 */
import React, { useState } from 'react';
import {
  Modal,
  PasswordInput,
  Button,
  Group,
  Stack,
  Alert,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconLock, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const ChangePasswordModal = ({ opened, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Заполните все поля');
      return;
    }

    if (newPassword.length < 8) {
      setError('Новый пароль должен содержать минимум 8 символов');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (currentPassword === newPassword) {
      setError('Новый пароль должен отличаться от текущего');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка при смене пароля');
      }

      notifications.show({
        title: 'Успешно',
        message: 'Пароль успешно изменён',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      handleClose();
    } catch (err) {
      const errorMessage = err.message || 'Ошибка при смене пароля';

      if (errorMessage === 'Current password is incorrect') {
        setError('Текущий пароль неверен');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconLock size={20} />
          <span>Смена пароля</span>
        </Group>
      }
      size="sm"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Введите текущий пароль и новый пароль для изменения
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          <PasswordInput
            label="Текущий пароль"
            placeholder="Введите текущий пароль"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />

          <PasswordInput
            label="Новый пароль"
            placeholder="Минимум 8 символов"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />

          <PasswordInput
            label="Подтверждение пароля"
            placeholder="Повторите новый пароль"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            error={
              confirmPassword && newPassword !== confirmPassword
                ? 'Пароли не совпадают'
                : null
            }
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              Сменить пароль
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
